import { Aide } from '@/components/ui/aide';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Paperclip, Plus } from 'lucide-react';

import { api, documentsAPI, driversAPI, operationsAPI, tripsAPI, trucksAPI, clientsAPI } from '../../lib/api';
import {
  CATEGORIES_SAISIE,
  cleFrais,
  enregistrerFrais,
  messageErreurFrais,
  optionClient,
  optionCompte,
  optionConvoi,
  optionTrajet,
} from '../../lib/expensesApi';
import { fetchAllPages } from '../../lib/fetchAllPages';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import { formatDate } from '../../lib/formats';
import { libelle, nomLisible, optionsLibelles } from '../../lib/libelles';
import { truckOption } from '../../lib/selectOptions';
import { AuthContext } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';

/**
 * Saisie d'un frais: un seul formulaire pour le trajet, le camion et le convoi.
 *
 * Étape du parcours: les frais viennent après le chargement et la vente, ils
 * pèsent sur la marge du camion (le calcul se lit sur la fiche du camion).
 *
 * - Le camion, le trajet et le convoi sont fixés par l'écran qui ouvre le
 *   formulaire (`truckId`, `tripId`, `convoyId`); sinon on les choisit dans des
 *   listes qu'on peut fouiller. Un trajet choisi donne son camion et son convoi.
 * - « Payé par »: le chauffeur sur son avance (si le trajet a un chauffeur), le
 *   propriétaire du camion (si un camion est connu: rien ne sort de la caisse de
 *   la société, donc aucun compte) ou la caisse / le compte (alors le compte est
 *   obligatoire).
 * - Frais supplémentaires: motif obligatoire, justificatif facultatif, et la
 *   question de savoir qui supporte le frais (société, propriétaire du camion,
 *   client à refacturer).
 * - L'envoi passe par la file durable: sans réseau le frais est gardé sur
 *   l'appareil, et la clé d'idempotence évite tout doublon au rejeu.
 *
 * Les champs tiennent sur peu de lignes: camion, trajet, convoi et catégorie;
 * nature, montant et date; payé par, compte, qui supporte (grilles qui se
 * répartissent selon la largeur).
 *
 * Aucun identifiant n'est affiché: les listes montrent plaques, numéros et noms.
 *
 * `colonneEtroite`: le formulaire loge dans la colonne de droite d'une fiche
 * (360 px à partir de xl). Les champs passent alors sur une seule colonne et le
 * bouton prend toute la largeur; la mise en page ne change pas ailleurs.
 */

const PAYE_PAR_CHAUFFEUR = 'CHAUFFEUR';
const PAYE_PAR_CAISSE = 'CAISSE';
const PAYE_PAR_PROPRIETAIRE = 'PROPRIETAIRE';

/** Ce que le contrat de location reçue peut prévoir comme porteur d'un frais. */
const PORTEURS_DU_CONTRAT = ['SOCIETE', 'PROPRIETAIRE'];

const NATURES_ROUTE = ['TOLL', 'WEIGH_STATION', 'CUSTOMS_FEE', 'POLICE_CHECK', 'MEALS', 'REPAIRS', 'OTHER'];

const champ = 'mt-1 block w-full rounded-md border bg-background p-2 text-sm';

// Les champs d'une ligne se partagent la largeur: trois sur un écran large, un seul sur un téléphone.
const GRILLE = 'grid gap-3 grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]';

const aujourdhui = () => {
  const now = new Date();
  const mois = String(now.getMonth() + 1).padStart(2, '0');
  const jour = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mois}-${jour}`;
};

const lignes = (response) =>
  response?.data?.data?.items || response?.data?.items || response?.data?.data || response?.data || [];

const etatInitial = ({ tripId, truckId, convoyId }) => ({
  truck_id: truckId || '',
  trip_id: tripId || '',
  convoy_id: convoyId || '',
  category: tripId ? 'FRAIS_ROUTE' : 'AUTRES',
  nature: 'OTHER',
  amount: '',
  date: aujourdhui(),
  paid_by: PAYE_PAR_CHAUFFEUR,
  account_id: '',
  notes: '',
  reason: '',
  bearer: 'SOCIETE',
  client_id: '',
  document: null,
});

export default function ExpenseForm({ truckId, tripId, convoyId, trip: tripProp, onSaved, colonneEtroite = false, enFenetre = false }) {
  const [form, setForm] = useState(() => etatInitial({ tripId, truckId, convoyId }));
  const [cle, setCle] = useState(() => cleFrais());
  const [erreurs, setErreurs] = useState({});
  const [erreurGlobale, setErreurGlobale] = useState('');
  const [info, setInfo] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [televersement, setTeleversement] = useState(false);
  const fichierRef = useRef(null);

  const [comptes, setComptes] = useState([]);
  const [camions, setCamions] = useState([]);
  const [convois, setConvois] = useState([]);
  const [clients, setClients] = useState([]);
  const [trajets, setTrajets] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [trajetFixe, setTrajetFixe] = useState(tripProp || null);

  // « Qui supporte ce frais »: sur un camion pris en location chez un transporteur,
  // le porteur est celui que son contrat prévoit pour cette catégorie, tant que
  // l'utilisateur n'a pas choisi lui-même. Sans contrat ni module, la société.
  const auth = useContext(AuthContext);
  const locationActive = typeof auth?.hasModule === 'function' && auth.hasModule('truck_rentals');
  const porteurChoisi = useRef(false);
  const [reprisePorteur, setReprisePorteur] = useState(0);

  const editable = (nom, valeur) => {
    setForm((ancien) => ({ ...ancien, [nom]: valeur }));
    setErreurs((ancien) => ({ ...ancien, [nom]: undefined }));
    // Une saisie modifiée est une nouvelle demande: nouvelle clé.
    setCle(cleFrais());
    setInfo('');
  };

  // Le trajet imposé par l'écran: on lit son chauffeur, son camion et son convoi.
  useEffect(() => {
    let actif = true;
    if (!tripId) {
      setTrajetFixe(null);
      return undefined;
    }
    if (tripProp && tripProp.id === tripId) {
      setTrajetFixe(tripProp);
      return undefined;
    }
    tripsAPI
      .getById(tripId)
      .then((trajet) => actif && setTrajetFixe(trajet))
      .catch(() => actif && setTrajetFixe(null));
    return () => {
      actif = false;
    };
  }, [tripId, tripProp]);

  // Les listes du formulaire: comptes de trésorerie toujours, le reste seulement
  // quand l'écran ne l'impose pas.
  useEffect(() => {
    let actif = true;
    const ok = (fn) => (valeur) => actif && fn(valeur);
    api
      .get('/finance/accounts')
      .then((r) => ok(setComptes)(lignes(r)))
      .catch(() => ok(setComptes)([]));
    if (!tripId && !truckId) {
      fetchAllPages((params) => trucksAPI.getAllPaged(params))
        .then((r) => ok(setCamions)(r.items))
        .catch(() => ok(setCamions)([]));
    } else if (truckId && !tripId) {
      trucksAPI
        .getById(truckId)
        .then((camion) => ok(setCamions)(camion ? [camion] : []))
        .catch(() => ok(setCamions)([]));
    }
    if (!convoyId && !tripId) {
      fetchAllPages((params) => operationsAPI.getAllPaged(params))
        .then((r) => ok(setConvois)(r.items))
        .catch(() => ok(setConvois)([]));
    } else if (convoyId) {
      operationsAPI
        .getById(convoyId)
        .then((convoi) => ok(setConvois)(convoi ? [convoi] : []))
        .catch(() => ok(setConvois)([]));
    }
    return () => {
      actif = false;
    };
  }, [tripId, truckId, convoyId]);

  // Clients et chauffeurs ne servent qu'à la refacturation et au nom du payeur.
  useEffect(() => {
    let actif = true;
    if (form.bearer === 'CLIENT' && clients.length === 0) {
      fetchAllPages((params) => clientsAPI.getAllPaged(params))
        .then((r) => actif && setClients(r.items))
        .catch(() => actif && setClients([]));
    }
    return () => {
      actif = false;
    };
  }, [form.bearer, clients.length]);

  // Trajets proposés: ceux du camion, ou du convoi, choisis ou imposés.
  const camionPourTrajets = tripId ? null : form.truck_id || truckId || '';
  const convoiPourTrajets = tripId ? null : convoyId || '';
  useEffect(() => {
    let actif = true;
    if (tripId || (!camionPourTrajets && !convoiPourTrajets)) {
      setTrajets([]);
      return undefined;
    }
    const filtre = convoiPourTrajets ? { operation_id: convoiPourTrajets } : { truck_id: camionPourTrajets };
    tripsAPI
      .getAll(filtre)
      .then((rows) => {
        if (!actif) return;
        const liste = Array.isArray(rows) ? rows : [];
        setTrajets(
          liste.filter((t) => (convoiPourTrajets ? t.operation_id === convoiPourTrajets : t.truck_id === camionPourTrajets)),
        );
      })
      .catch(() => actif && setTrajets([]));
    return () => {
      actif = false;
    };
  }, [tripId, camionPourTrajets, convoiPourTrajets]);

  // Le trajet effectif: imposé, ou choisi dans la liste.
  const trajet = useMemo(() => {
    if (tripId) return trajetFixe;
    if (!form.trip_id) return null;
    return trajets.find((t) => t.id === form.trip_id) || null;
  }, [tripId, trajetFixe, form.trip_id, trajets]);

  useEffect(() => {
    let actif = true;
    const idChauffeur = trajet?.driver_id;
    if (idChauffeur && !chauffeurs.some((d) => d.id === idChauffeur)) {
      driversAPI
        .getById(idChauffeur)
        .then((c) => actif && c && setChauffeurs((ancien) => [...ancien, c]))
        .catch(() => {});
    }
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trajet?.driver_id]);

  const chauffeur = trajet?.driver_id ? chauffeurs.find((d) => d.id === trajet.driver_id) || null : null;
  const chauffeurDisponible = Boolean(trajet?.driver_id);
  const camionConnu = Boolean(truckId || trajet?.truck_id || form.truck_id);
  let payeur = chauffeurDisponible ? form.paid_by : PAYE_PAR_CAISSE;
  if (form.paid_by === PAYE_PAR_PROPRIETAIRE) payeur = camionConnu ? PAYE_PAR_PROPRIETAIRE : PAYE_PAR_CAISSE;

  const camionDuFrais = truckId || trajet?.truck_id || form.truck_id || '';
  useEffect(() => {
    if (!locationActive || !camionDuFrais || !form.category || !form.date) return undefined;
    let actif = true;
    inboundRentalsAPI
      .getCostBearer(camionDuFrais, form.category, form.date)
      .then((trouve) => {
        if (!actif || porteurChoisi.current) return;
        if (trouve?.contract_id && PORTEURS_DU_CONTRAT.includes(trouve.cost_bearer)) {
          setForm((ancien) => (ancien.bearer === trouve.cost_bearer ? ancien : { ...ancien, bearer: trouve.cost_bearer }));
        }
      })
      .catch(() => {
        // Sans réponse du serveur, la société reste le porteur par défaut.
      });
    return () => {
      actif = false;
    };
  }, [locationActive, camionDuFrais, form.category, form.date, reprisePorteur]);

  const camionsOptions = useMemo(() => camions.map(truckOption), [camions]);
  const convoisOptions = useMemo(() => convois.map(optionConvoi), [convois]);
  const trajetsOptions = useMemo(() => trajets.map(optionTrajet), [trajets]);
  const comptesOptions = useMemo(() => comptes.map(optionCompte), [comptes]);
  const clientsOptions = useMemo(() => clients.map(optionClient), [clients]);
  const categories = useMemo(() => optionsLibelles('categorie_frais', CATEGORIES_SAISIE), []);
  const natures = useMemo(() => optionsLibelles('type_frais_route', NATURES_ROUTE), []);

  const porteursOfferts = form.category === 'FRAIS_SUPP' ? ['SOCIETE', 'PROPRIETAIRE', 'CLIENT'] : ['SOCIETE', 'PROPRIETAIRE'];

  // Choisir un trajet fixe son camion et son convoi (le serveur les déduit aussi).
  const choisirTrajet = (valeur) => {
    porteurChoisi.current = false;
    const choisi = trajets.find((t) => t.id === valeur);
    setForm((ancien) => ({
      ...ancien,
      trip_id: valeur,
      truck_id: choisi?.truck_id || ancien.truck_id,
      convoy_id: choisi?.operation_id || (convoyId ? ancien.convoy_id : ''),
      paid_by: choisi?.driver_id ? PAYE_PAR_CHAUFFEUR : PAYE_PAR_CAISSE,
    }));
    setErreurs((ancien) => ({ ...ancien, trip_id: undefined }));
    setCle(cleFrais());
  };

  const changerCategorie = (valeur) => {
    porteurChoisi.current = false;
    setForm((ancien) => ({
      ...ancien,
      category: valeur,
      bearer: ancien.bearer === 'CLIENT' && valeur !== 'FRAIS_SUPP' ? 'SOCIETE' : ancien.bearer,
    }));
    setErreurs((ancien) => ({ ...ancien, category: undefined }));
    setCle(cleFrais());
  };

  const joindre = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > 10 * 1024 * 1024) {
      setErreurs((ancien) => ({ ...ancien, document: 'Fichier trop lourd : 10 Mo au maximum.' }));
      return;
    }
    setTeleversement(true);
    try {
      const envoye = await documentsAPI.upload(fichier, 'RECEIPT', { capture_source: 'web_operator' });
      setForm((ancien) => ({ ...ancien, document: { id: envoye?.id, nom: fichier.name } }));
      setErreurs((ancien) => ({ ...ancien, document: undefined }));
      setCle(cleFrais());
    } catch (error) {
      setErreurs((ancien) => ({
        ...ancien,
        document: 'Le justificatif n’a pas pu être envoyé. Réessayez dans un instant.',
      }));
    } finally {
      setTeleversement(false);
    }
  };

  const valider = () => {
    const e = {};
    const montant = Number(form.amount);
    if (!form.amount || !Number.isFinite(montant) || montant <= 0) e.amount = 'Indiquez un montant supérieur à zéro.';
    if (!form.date) e.date = 'Indiquez la date du frais.';
    if (!form.category) e.category = 'Choisissez la catégorie du frais.';
    if (form.category === 'FRAIS_SUPP' && !form.reason.trim()) {
      e.reason = 'Indiquez le motif du frais supplémentaire.';
    }
    if (payeur === PAYE_PAR_CAISSE && !form.account_id) {
      e.account_id = 'Choisissez la caisse ou le compte qui a payé.';
    }
    if (form.bearer === 'CLIENT' && !tripId && !form.trip_id && !form.client_id) {
      e.client_id = 'Choisissez le client à qui refacturer ce frais.';
    }
    return e;
  };

  const enregistrer = async () => {
    setErreurGlobale('');
    setInfo('');
    const e = valider();
    setErreurs(e);
    if (Object.values(e).some(Boolean)) return;

    const idTrajet = tripId || form.trip_id || '';
    const details = {};
    if (form.notes.trim()) details.notes = form.notes.trim();
    if (form.reason.trim()) details.reason = form.reason.trim();
    if (form.category === 'FRAIS_ROUTE' && form.nature) details.expense_type = form.nature;
    if (form.bearer === 'CLIENT' && form.client_id) details.client_id = form.client_id;
    if (payeur === PAYE_PAR_PROPRIETAIRE) details.paid_by = 'OWNER';

    const corps = {
      idempotency_key: cle,
      kind: payeur === PAYE_PAR_CHAUFFEUR ? 'DRIVER_EXPENSE' : 'EXPENSE',
      amount: Number(form.amount),
      currency: 'XOF',
      category: form.category,
      cost_bearer: form.bearer,
      occurred_at: form.date,
      details,
    };
    if (payeur === PAYE_PAR_CHAUFFEUR) {
      corps.party_type = 'DRIVER';
      corps.party_id = trajet.driver_id;
    } else if (payeur === PAYE_PAR_CAISSE) {
      corps.account_id = form.account_id;
    }
    // Un trajet donne son camion et son convoi : les envoyer en plus ne ferait
    // que risquer un désaccord.
    if (idTrajet) {
      corps.trip_id = idTrajet;
    } else {
      const idCamion = truckId || form.truck_id;
      const idConvoi = convoyId || form.convoy_id;
      if (idCamion) corps.truck_id = idCamion;
      if (idConvoi) corps.convoy_id = idConvoi;
    }
    if (form.document?.id) corps.document_id = form.document.id;

    setEnvoi(true);
    try {
      const resultat = await enregistrerFrais(corps);
      setInfo('Frais enregistré.');
      setForm((ancien) => ({
        ...etatInitial({ tripId, truckId, convoyId }),
        truck_id: ancien.truck_id,
        trip_id: ancien.trip_id,
        convoy_id: ancien.convoy_id,
        category: ancien.category,
        date: ancien.date,
        paid_by: ancien.paid_by,
        account_id: ancien.account_id,
      }));
      setCle(cleFrais());
      porteurChoisi.current = false;
      setReprisePorteur((v) => v + 1);
      if (fichierRef.current) fichierRef.current.value = '';
      if (onSaved) await onSaved(resultat);
    } catch (error) {
      setErreurGlobale(messageErreurFrais(error));
    } finally {
      setEnvoi(false);
    }
  };

  const grille = cn(GRILLE, colonneEtroite && 'xl:grid-cols-1');
  const nomChauffeur = chauffeur ? nomLisible(chauffeur.full_name) : null;
  const camionFixe = camions.find((c) => c.id === (truckId || trajet?.truck_id)) || null;
  const convoiFixe = convois.find((c) => c.id === (convoyId || trajet?.operation_id)) || null;

  return (
    <div className={cn('space-y-3', !enFenetre && 'rounded-lg border bg-card p-4')} aria-label="Saisie d’un frais" data-testid="expense-form">
      {/* Dans une fenêtre (« Nouvelle opération »), le titre est celui de la fenêtre. */}
      {!enFenetre && <div>
        <div className="flex items-center gap-1">
          <h3 className="text-base font-semibold">Ajouter un frais</h3>
          <Aide titre="Ajouter un frais">Un frais par ligne. Il s’ajoute aux coûts du camion et du trajet.</Aide>
        </div>
      </div>}

      {erreurGlobale && (
        <p role="alert" className="text-sm text-destructive">
          {erreurGlobale}
        </p>
      )}
      {info && (
        <p role="status" className="text-sm text-muted-foreground">
          {info}
        </p>
      )}

      <div className={grille}>
        {/* Camion */}
        <div>
          <Label htmlFor="frais-camion">Camion</Label>
          {truckId || tripId ? (
            <p id="frais-camion" className="mt-2 text-sm font-medium">
              {camionFixe?.matricule || trajet?.tractor_plate || 'Camion du trajet'}
            </p>
          ) : (
            <SearchableSelect
              id="frais-camion"
              aria-label="Camion"
              className="mt-1 rounded-md border bg-background p-2"
              value={form.truck_id}
              onValueChange={(valeur) => {
                porteurChoisi.current = false;
                editable('truck_id', valeur);
                setForm((ancien) => ({ ...ancien, trip_id: '' }));
              }}
              options={camionsOptions}
              placeholder="Choisir un camion (facultatif)"
              searchPlaceholder="Rechercher par plaque ou chauffeur…"
              emptyMessage="Aucun camion trouvé"
            />
          )}
        </div>

        {/* Trajet */}
        <div>
          <Label htmlFor="frais-trajet">Trajet</Label>
          {tripId ? (
            <p id="frais-trajet" className="mt-2 text-sm font-medium">
              {trajet ? optionTrajet(trajet).label : 'Trajet en cours'}
            </p>
          ) : (
            <SearchableSelect
              id="frais-trajet"
              aria-label="Trajet"
              className="mt-1 rounded-md border bg-background p-2"
              value={form.trip_id}
              onValueChange={choisirTrajet}
              options={trajetsOptions}
              placeholder={camionPourTrajets || convoiPourTrajets ? 'Choisir un trajet (facultatif)' : 'Choisissez d’abord un camion'}
              searchPlaceholder="Rechercher un trajet…"
              emptyMessage="Aucun trajet pour ce camion"
              disabled={!camionPourTrajets && !convoiPourTrajets}
            />
          )}
        </div>

        {/* Convoi */}
        <div>
          <Label htmlFor="frais-convoi">Convoi</Label>
          {convoyId || tripId ? (
            <p id="frais-convoi" className="mt-2 text-sm font-medium">
              {convoiFixe ? optionConvoi(convoiFixe).label : trajet?.operation_id ? 'Convoi du trajet' : 'Aucun convoi'}
            </p>
          ) : (
            <SearchableSelect
              id="frais-convoi"
              aria-label="Convoi"
              className="mt-1 rounded-md border bg-background p-2"
              value={form.trip_id ? trajet?.operation_id || '' : form.convoy_id}
              onValueChange={(valeur) => editable('convoy_id', valeur)}
              options={convoisOptions}
              placeholder="Choisir un convoi (facultatif)"
              searchPlaceholder="Rechercher un convoi…"
              emptyMessage="Aucun convoi trouvé"
              disabled={Boolean(form.trip_id)}
            />
          )}
        </div>

        {/* Catégorie */}
        <div>
          <Label htmlFor="frais-categorie">Catégorie</Label>
          <select
            id="frais-categorie"
            className={champ}
            value={form.category}
            onChange={(event) => changerCategorie(event.target.value)}
            aria-invalid={Boolean(erreurs.category)}
          >
            {categories.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <FieldError show={Boolean(erreurs.category)}>{erreurs.category}</FieldError>
        </div>

      </div>

      <div className={grille}>
        {form.category === 'FRAIS_ROUTE' && (
          <div>
            <Label htmlFor="frais-nature">Nature du frais de route</Label>
            <select
              id="frais-nature"
              className={champ}
              value={form.nature}
              onChange={(event) => editable('nature', event.target.value)}
            >
              {natures.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Montant et date */}
        <div>
          <Label htmlFor="frais-montant">Montant (FCFA)</Label>
          <Input
            id="frais-montant"
            type="number"
            inputMode="numeric"
            min="1"
            className="mt-1 text-right"
            value={form.amount}
            onChange={(event) => editable('amount', event.target.value)}
            aria-invalid={Boolean(erreurs.amount)}
          />
          <FieldError show={Boolean(erreurs.amount)}>{erreurs.amount}</FieldError>
        </div>
        <div>
          <Label htmlFor="frais-date">Date</Label>
          <Input
            id="frais-date"
            type="date"
            className="mt-1"
            value={form.date}
            onChange={(event) => editable('date', event.target.value)}
            aria-invalid={Boolean(erreurs.date)}
          />
          <FieldError show={Boolean(erreurs.date)}>{erreurs.date}</FieldError>
        </div>

      </div>

      <div className={grille}>
        {/* Payé par */}
        <div>
          <Label htmlFor="frais-paye-par">Payé par</Label>
          <select
            id="frais-paye-par"
            className={champ}
            value={payeur}
            onChange={(event) => {
              editable('paid_by', event.target.value);
              // Le propriétaire a payé: c'est lui qui supporte le frais, sauf choix contraire.
              if (event.target.value === PAYE_PAR_PROPRIETAIRE && form.bearer === 'SOCIETE') {
                porteurChoisi.current = true;
                editable('bearer', 'PROPRIETAIRE');
              }
            }}
          >
            {chauffeurDisponible && (
              <option value={PAYE_PAR_CHAUFFEUR}>
                {nomChauffeur ? `Le chauffeur, sur son avance (${nomChauffeur})` : 'Le chauffeur, sur son avance'}
              </option>
            )}
            <option value={PAYE_PAR_CAISSE}>La caisse ou un compte de la société</option>
            {camionConnu && <option value={PAYE_PAR_PROPRIETAIRE}>Le propriétaire du camion</option>}
          </select>
          {!chauffeurDisponible && (tripId || form.trip_id) && (
            <p className="mt-1 text-xs text-muted-foreground">Ce trajet n’a pas de chauffeur : le frais est payé par la caisse.</p>
          )}
        </div>
        {payeur === PAYE_PAR_CAISSE && (
          <div>
            <Label htmlFor="frais-compte">Compte</Label>
            <SearchableSelect
              id="frais-compte"
              aria-label="Compte"
              className="mt-1 rounded-md border bg-background p-2"
              value={form.account_id}
              onValueChange={(valeur) => editable('account_id', valeur)}
              options={comptesOptions}
              placeholder="Choisir la caisse ou le compte"
              searchPlaceholder="Rechercher un compte…"
              emptyMessage="Aucun compte trouvé"
              aria-invalid={Boolean(erreurs.account_id)}
            />
            <FieldError show={Boolean(erreurs.account_id)}>{erreurs.account_id}</FieldError>
          </div>
        )}

        {/* Qui supporte le frais */}
        <div>
          <Label htmlFor="frais-porteur">Qui supporte ce frais</Label>
          <select
            id="frais-porteur"
            className={champ}
            value={form.bearer}
            onChange={(event) => {
              porteurChoisi.current = true;
              editable('bearer', event.target.value);
            }}
          >
            {porteursOfferts.map((code) => (
              <option key={code} value={code}>
                {libelle('porteur_frais', code)}
              </option>
            ))}
          </select>
        </div>
        {form.bearer === 'CLIENT' && (
          <div>
            <div className="flex items-center gap-1">
              <Label htmlFor="frais-client">Client à refacturer</Label>
              <Aide titre="Client à refacturer">
                Si le trajet sert plusieurs clients, choisissez celui à qui refacturer.
              </Aide>
            </div>
            <SearchableSelect
              id="frais-client"
              aria-label="Client à refacturer"
              className="mt-1 rounded-md border bg-background p-2"
              value={form.client_id}
              onValueChange={(valeur) => editable('client_id', valeur)}
              options={clientsOptions}
              placeholder={tripId || form.trip_id ? 'Le client du trajet (si un seul)' : 'Choisir un client'}
              searchPlaceholder="Rechercher un client…"
              emptyMessage="Aucun client trouvé"
              aria-invalid={Boolean(erreurs.client_id)}
            />
            <FieldError show={Boolean(erreurs.client_id)}>{erreurs.client_id}</FieldError>
          </div>
        )}
      </div>

      {form.category === 'FRAIS_SUPP' && (
        <div className={grille}>
          <div>
            <Label htmlFor="frais-motif">Motif du frais supplémentaire</Label>
            <Input
              id="frais-motif"
              className="mt-1"
              placeholder="Ex. amende de pesage"
              value={form.reason}
              onChange={(event) => editable('reason', event.target.value)}
              aria-invalid={Boolean(erreurs.reason)}
            />
            <FieldError show={Boolean(erreurs.reason)}>{erreurs.reason}</FieldError>
          </div>
          <div>
            <Label htmlFor="frais-justificatif">Justificatif (photo ou PDF)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id="frais-justificatif"
                ref={fichierRef}
                type="file"
                accept="image/*,application/pdf"
                className="sr-only"
                disabled={televersement}
                onChange={(event) => joindre(event.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={televersement}
                onClick={() => fichierRef.current?.click()}
              >
                {televersement ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Paperclip className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                {form.document ? 'Changer le fichier' : 'Joindre un fichier'}
              </Button>
              {form.document && <span className="truncate text-sm text-muted-foreground">{form.document.nom}</span>}
            </div>
            <FieldError show={Boolean(erreurs.document)}>{erreurs.document}</FieldError>
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="frais-note">Note (facultatif)</Label>
        <Textarea
          id="frais-note"
          rows={2}
          className="mt-1"
          value={form.notes}
          onChange={(event) => editable('notes', event.target.value)}
        />
      </div>

      <div className={cn('flex flex-wrap items-center justify-between gap-3', colonneEtroite && 'justify-end')}>
        {/* Dans la colonne étroite, la date est juste au-dessus: la répéter
            ferait une ligne de plus sans rien dire de neuf. */}
        <p className={cn('text-xs text-muted-foreground', colonneEtroite && 'hidden')}>
          {form.date ? `Frais du ${formatDate(form.date)}.` : ''}
        </p>
        <Button
          type="button"
          className={cn(colonneEtroite && 'w-full sm:w-auto xl:w-full')}
          onClick={enregistrer}
          disabled={envoi || televersement}
        >
          {envoi ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {envoi ? 'Enregistrement…' : 'Enregistrer le frais'}
        </Button>
      </div>
    </div>
  );
}
